package ai.jessie.web.profile

import ai.jessie.shared.profile.GlobalProfileRequest
import ai.jessie.shared.profile.MembershipProfileRequest
import ai.jessie.shared.profile.OwnProfileResponse
import ai.jessie.shared.profile.ProfileProposalRequest

// Field names match the server's `fields`: `global.*` and `membership.*` never share a key.
data class ProfileDraft(val fields: Map<String, String>) {
    // Only scopes the user actually edited are proposed, so an untouched scope keeps its revision.
    fun proposal(current: OwnProfileResponse): ProfileProposalRequest {
        val baseline = current.draft().fields
        fun edited(scope: String) = fields.any { (name, value) ->
            name.startsWith(scope) && baseline[name] != value
        }
        val membership = current.membership
        return ProfileProposalRequest(
            global =
                if (edited("global."))
                    GlobalProfileRequest(
                        revision = current.global.revision,
                        firstName = fields.getValue(FIRST_NAME),
                        lastName = fields.getValue(LAST_NAME),
                        displayName = fields.getValue(DISPLAY_NAME),
                        locale = fields.getValue(LOCALE),
                    )
                else null,
            membership =
                if (membership != null && edited("membership."))
                    MembershipProfileRequest(
                        organisationId = membership.organisationId,
                        revision = membership.revision,
                        displayName = fields.getValue(MEMBER_DISPLAY_NAME),
                        commentEmailNotifications = fields.getValue(COMMENT_EMAIL) == "true",
                    )
                else null,
        )
    }
}

// A2: without a selected membership no membership field exists in the draft at all.
internal fun OwnProfileResponse.draft() =
    ProfileDraft(
        linkedMapOf(
            FIRST_NAME to global.firstName,
            LAST_NAME to global.lastName,
            DISPLAY_NAME to global.displayName,
            LOCALE to global.locale,
        ) +
            membership
                ?.let {
                    mapOf(
                        MEMBER_DISPLAY_NAME to it.displayName.orEmpty(),
                        COMMENT_EMAIL to it.commentEmailNotifications.toString(),
                    )
                }
                .orEmpty()
    )

data class ProfileConflict(val field: String, val current: String?, val intended: String?)

// A5: what is stored now against what the user intended, limited to the conflicting scopes.
internal fun profileConflicts(
    scopes: List<String>,
    current: OwnProfileResponse,
    intended: ProfileDraft,
): List<ProfileConflict> {
    val stored = current.draft().fields
    return intended.fields
        .filter { (name, value) ->
            name.substringBefore('.') in scopes && name in stored && stored[name] != value
        }
        .map { (name, value) -> ProfileConflict(name, stored[name], value) }
}

internal const val FIRST_NAME = "global.firstName"
internal const val LAST_NAME = "global.lastName"
internal const val DISPLAY_NAME = "global.displayName"
internal const val LOCALE = "global.locale"
internal const val MEMBER_DISPLAY_NAME = "membership.displayName"
internal const val COMMENT_EMAIL = "membership.commentEmailNotifications"
